import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion, useMotionValue } from "framer-motion";
import { KineticLines, SectionLabel } from "../Reveal";

const GATES = [
  { label: "Operating costs", y: 142 },
  { label: "Technology expenses", y: 222 },
  { label: "Employee benefits", y: 302 },
  { label: "Taxes", y: 382 },
];

const SPINE = "M170 240 C250 240 270 142 356 142 C440 142 285 222 356 222 C440 222 285 302 356 302 C440 302 285 382 356 382 C540 382 700 240 872 240";
const SPINE_V = "M200 84 C200 120 200 130 200 168 C200 210 200 220 200 262 C200 304 200 314 200 356 C200 400 200 430 200 470 C200 520 200 540 200 560";

function Gate({ g, glow }) {
  return (
    <g>
      <rect x="372" y={g.y - 22} width="296" height="44" rx="12" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.4" />
      <motion.rect x="372" y={g.y - 22} width="296" height="44" rx="12" fill="none" stroke="#15B8A6" strokeWidth="1.7" style={{ opacity: glow }} />
      <text x="392" y={g.y + 4} fontSize="13" fontWeight="700" fill="#101F38" style={{ fontFamily: "Inter, sans-serif" }}>{g.label}</text>
      <circle cx="646" cy={g.y} r="9" fill="#EAF4F8" stroke="#B9CFE4" strokeWidth="1.4" />
      <motion.circle cx="646" cy={g.y} r="9" fill="#15B8A6" stroke="#0E9384" strokeWidth="1.2" style={{ opacity: glow }} />
    </g>
  );
}

export default function ExpenseFlow() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.5"] });
  const draw = useTransform(scrollYProgress, [0.05, 0.75], [0, 1]);
  const alwaysOne = useMotionValue(1);
  const src = reduced ? alwaysOne : scrollYProgress;
  const g1 = useTransform(src, [0.16, 0.26], [0, 1]);
  const g2 = useTransform(src, [0.3, 0.4], [0, 1]);
  const g3 = useTransform(src, [0.44, 0.54], [0, 1]);
  const g4 = useTransform(src, [0.58, 0.68], [0, 1]);
  const end = useTransform(src, [0.8, 0.95], [0, 1]);
  const glows = [g1, g2, g3, g4];

  return (
    <section ref={ref} className="relative py-16 md:py-24 bg-offwhite overflow-hidden" data-testid="expense-flow-section">
      <div className="absolute inset-0 bg-arch-fine opacity-60" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <SectionLabel>The Philosophy</SectionLabel>
          <KineticLines
            as="h2"
            className="mt-5 font-display font-extrabold tracking-tightest text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.08]"
            lines={["It's Not Just About What You Make.", "It's About What You Keep."]}
          />
          <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-slate">
            <p>For over 25 years, we've helped businesses grow.</p>
            <p>But along the way, we discovered something important.</p>
            <p className="text-navy font-semibold">
              Sometimes improving profitability isn't about making another sale. It's about finding opportunities
              hiding inside the business you already have.
            </p>
          </div>
        </div>

        {/* Desktop flow */}
        <div className="hidden md:block mt-12">
          <svg viewBox="0 0 1040 480" className="w-full h-auto" role="img" aria-label="Animated financial flow: revenue passes through operating costs, technology, benefits and taxes toward a healthier operation">
            {/* base faint lines */}
            {GATES.map((g) => (
              <path key={g.label} d={`M170 240 C250 240 270 ${g.y} 372 ${g.y}`} fill="none" stroke="#C9DBEA" strokeWidth="1.6" strokeDasharray="3 6" />
            ))}
            {GATES.map((g) => (
              <path key={g.label + "-o"} d={`M668 ${g.y} C760 ${g.y} 790 240 872 240`} fill="none" stroke="#C9DBEA" strokeWidth="1.6" strokeDasharray="3 6" />
            ))}

            {/* source */}
            <rect x="20" y="200" width="150" height="80" rx="16" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.4" />
            <text x="95" y="235" textAnchor="middle" fontSize="11" fontWeight="800" letterSpacing="1.6" fill="#234D85" style={{ fontFamily: "Inter, sans-serif" }}>REVENUE IN</text>
            <text x="95" y="256" textAnchor="middle" fontSize="10" fill="#8A97A6" style={{ fontFamily: "Inter, sans-serif" }}>What the business earns</text>

            {/* system panel */}
            <rect x="330" y="76" width="380" height="330" rx="24" fill="#EAF4F8" stroke="#DCE7EF" strokeWidth="1.4" />
            <text x="520" y="108" textAnchor="middle" fontSize="11" fontWeight="800" letterSpacing="2" fill="#234D85" style={{ fontFamily: "Inter, sans-serif" }}>THE BUSINESS, AS IT IS</text>

            {GATES.map((g, i) => (
              <Gate key={g.label} g={g} glow={glows[i]} />
            ))}

            {/* animated spine */}
            <motion.path d={SPINE} fill="none" stroke="#15B8A6" strokeWidth="2.6" strokeLinecap="round" style={{ pathLength: draw }} />
            {!reduced && (
              <>
                <circle r="4.5" fill="#15B8A6">
                  <animateMotion dur="5s" repeatCount="indefinite" path={SPINE} />
                </circle>
                <circle r="3" fill="#2BD4C1" opacity="0.7">
                  <animateMotion dur="5s" begin="-2.4s" repeatCount="indefinite" path={SPINE} />
                </circle>
              </>
            )}

            {/* destination */}
            <motion.g style={{ opacity: end }}>
              <rect x="872" y="196" width="150" height="88" rx="16" fill="#DDF3EA" stroke="#0F9D77" strokeWidth="1.6" />
              <text x="947" y="232" textAnchor="middle" fontSize="10.5" fontWeight="800" letterSpacing="1.2" fill="#0F9D77" style={{ fontFamily: "Inter, sans-serif" }}>HEALTHIER</text>
              <text x="947" y="248" textAnchor="middle" fontSize="10.5" fontWeight="800" letterSpacing="1.2" fill="#0F9D77" style={{ fontFamily: "Inter, sans-serif" }}>OPERATION</text>
              <text x="947" y="268" textAnchor="middle" fontSize="9" fill="#0F9D77" style={{ fontFamily: "Inter, sans-serif" }}>after thoughtful review</text>
            </motion.g>
          </svg>
        </div>

        {/* Mobile vertical flow */}
        <div className="md:hidden mt-10">
          <svg viewBox="0 0 400 640" className="w-full h-auto" role="img" aria-label="Financial flow diagram, vertical layout">
            <rect x="100" y="20" width="200" height="64" rx="14" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.4" />
            <text x="200" y="48" textAnchor="middle" fontSize="11" fontWeight="800" letterSpacing="1.4" fill="#234D85" style={{ fontFamily: "Inter, sans-serif" }}>REVENUE IN</text>
            <text x="200" y="66" textAnchor="middle" fontSize="9.5" fill="#8A97A6" style={{ fontFamily: "Inter, sans-serif" }}>What the business earns</text>

            <rect x="40" y="128" width="320" height="332" rx="20" fill="#EAF4F8" stroke="#DCE7EF" strokeWidth="1.4" />
            <text x="200" y="156" textAnchor="middle" fontSize="10" fontWeight="800" letterSpacing="1.8" fill="#234D85" style={{ fontFamily: "Inter, sans-serif" }}>THE BUSINESS, AS IT IS</text>
            {GATES.map((g, i) => {
              const y = 196 + i * 76;
              return (
                <g key={g.label}>
                  <rect x="64" y={y - 18} width="272" height="40" rx="11" fill="#FFFFFF" stroke="#DCE7EF" strokeWidth="1.3" />
                  <text x="80" y={y + 5} fontSize="12" fontWeight="700" fill="#101F38" style={{ fontFamily: "Inter, sans-serif" }}>{g.label}</text>
                  <circle cx="312" cy={y + 1} r="7" fill={i < 2 ? "#15B8A6" : "#EAF4F8"} stroke={i < 2 ? "#0E9384" : "#B9CFE4"} strokeWidth="1.3" />
                </g>
              );
            })}
            <motion.path d={SPINE_V} fill="none" stroke="#15B8A6" strokeWidth="2.4" strokeLinecap="round" style={{ pathLength: draw }} />

            <rect x="80" y="512" width="240" height="76" rx="16" fill="#DDF3EA" stroke="#0F9D77" strokeWidth="1.6" />
            <text x="200" y="544" textAnchor="middle" fontSize="10.5" fontWeight="800" letterSpacing="1.2" fill="#0F9D77" style={{ fontFamily: "Inter, sans-serif" }}>HEALTHIER OPERATION</text>
            <text x="200" y="564" textAnchor="middle" fontSize="9" fill="#0F9D77" style={{ fontFamily: "Inter, sans-serif" }}>after thoughtful review</text>

            <text x="200" y="622" textAnchor="middle" fontSize="10" fill="#8A97A6" style={{ fontFamily: "Inter, sans-serif" }}>Conceptual illustration — every business is different.</text>
          </svg>
        </div>

        <KineticLines
          as="p"
          className="mt-12 md:mt-16 font-display font-extrabold tracking-tightest text-navy text-2xl sm:text-3xl lg:text-4xl max-w-3xl leading-[1.15]"
          lines={["A stronger bottom line can start", "with a smarter look at your business."]}
        />
      </div>
    </section>
  );
}
